<script>
	import Button from '@smui/button';
	import Card from '@smui/card';
	import Select, { Option } from '@smui/select';
	import Tab, { Icon, Label } from '@smui/tab';
	import TabBar from '@smui/tab-bar';
	import Input from '@smui/textfield';
	import { _ } from 'svelte-i18n';
	import StepWizard from 'svelte-step-wizard';
	import { writable } from 'svelte/store';
	import FieldLoading from '../../../../shared/components/common/Loading/FieldLoading.svelte';
	import FormLoading from '../../../../shared/components/common/Loading/FormLoading.svelte';
	import SurveyReport from '../../../../shared/components/common/Survey/SurveyReport.svelte';
	import QuestionarioRisposte from '../../../../shared/models/Questionario/QuestionarioRisposte';
	import Sede from '../../../../shared/models/Sede/Sede';

    export let params = {
        id: '0',
    };

    let [data, loading, error] = QuestionarioRisposte.getByAnonymous(params.id);
    let [dataSedi, loadingSedi, errorSedi] = [writable({ aziendaEnteId: $data?.aziendaEnteId }), writable(false), null];
    let [dataQuestionarioCompletato, loadingQuestionarioCompletato, errorQuestionarioCompletato] = [null, writable(false), null];

    let jsonData = '';
    let jsonResponses = [];
    let jsonError = null;
    let jsonErrorPosition = null;
    let persistentHelperText = '';

    let tabs = [
        {
            icon: 'poll',
            label: 'Report',
        },
        {
            icon: 'data_object',
            label: 'JSON',
        }
    ];

    let active = tabs[0];
    let surveyReportComponent;
    let isEditingJson = false;

    let selectedSede = {};
    data.subscribe((value) => {
        if (value?.aziendaEnteId && !selectedSede?.id) {
            [dataSedi, loadingSedi, errorSedi] = Sede.getListByAnonymous({ aziendaEnteId: value.aziendaEnteId });
        }
    });

    $: selectedSede = $data?.sedeId ? $dataSedi?.response?.find((x) => x.id === $data.sedeId) : selectedSede;
    $: if ($data && $dataSedi?.response?.length == 1) {
        selectedSede = $dataSedi.response[0];
        $data.sedeId = selectedSede.id;
    }

    $: {
        if ($data && $data.questionarioRisposteJson && !isEditingJson) {
            try {
                let parsedJson = JSON.parse($data.questionarioRisposteJson);
                jsonResponses = Array.isArray(parsedJson) ? parsedJson : [parsedJson];
                jsonData = JSON.stringify(jsonResponses, null, 2);
                if (surveyReportComponent) {
                    surveyReportComponent.updateData(jsonResponses);
                }
            } catch (e) {
                console.error('Error parsing JSON:', e);
                jsonData = $data.questionarioRisposteJson || '';
                jsonError = e.message;
            }
        }
    }

    function submit() {
        if (validateJson(jsonData)) {
            const jsonResponsesStrings = jsonResponses.map(item => JSON.stringify(item));
            return ([dataQuestionarioCompletato, loadingQuestionarioCompletato, errorQuestionarioCompletato] =
                QuestionarioRisposte.saveMultiplo($data, jsonResponsesStrings));
        } else {
            // Handle invalid JSON error
            console.error('Cannot submit: Invalid JSON');
            return Promise.reject(new Error('Invalid JSON'));
        }
    }

    function validateJson(json) {
        try {
            const parsedJson = JSON.parse(json);
            if (!Array.isArray(parsedJson)) {
                throw new Error('Il JSON deve essere un array di oggetti');
            }
            jsonResponses = parsedJson;
            jsonError = null;
            jsonErrorPosition = null;
            persistentHelperText = 'JSON valido: array di oggetti';
            return true;
        } catch (e) {
            jsonError = e.message;
            const match = e.message.match(/position (\d+)/);
            jsonErrorPosition = match ? parseInt(match[1]) : null;
            persistentHelperText = `Errore JSON: ${jsonError}${jsonErrorPosition !== null ? ` (posizione: ${jsonErrorPosition})` : ''}`;
            return false;
        }
    }

    function handleJsonChange(event) {
        isEditingJson = true;
        const newValue = event.target.value;
        jsonData = newValue;
        if (validateJson(newValue) && surveyReportComponent) {
            surveyReportComponent.updateData(jsonResponses);
        }
        isEditingJson = false;
    }

    function handleTabChange(newTab) {
        active = newTab;
        if (newTab.label === 'JSON') {
            jsonData = JSON.stringify(jsonResponses, null, 2);
        } else if (newTab.label === 'Report' && !jsonError) {
            if (surveyReportComponent) {
                surveyReportComponent.updateData(jsonResponses);
            }
        }
    }

    function formatJson() {
        try {
            const parsedJson = JSON.parse(jsonData);
            jsonData = JSON.stringify(parsedJson, null, 2);
            validateJson(jsonData);
        } catch (e) {
            console.error('Cannot format: Invalid JSON');
        }
    }

    function isQuestionarioAttivo(questionario) {
        if (!questionario || !questionario.dataInizioValidita || !questionario.dataFineValidita) {
            return false;
        }
        
        const now = new Date();
        const dataInizio = new Date(questionario.dataInizioValidita);
        const dataFine = new Date(questionario.dataFineValidita);
        
        return now >= dataInizio && now <= dataFine;
    }
</script>

<Card class="md:px-8 md:py-8" padded>
    {#if $loading}
        <FormLoading />
    {:else if $dataQuestionarioCompletato && $dataQuestionarioCompletato.id > 0}
        <div class="pt-4 text-center">
            <span class="pt-4 text-2xl">{$_('page.survey.detail.survey_completed')}</span>
        </div>
    {:else}
        {#if $data && !isQuestionarioAttivo($data)}
            <div class="bg-yellow-50 p-4 pt-4 mb-4 text-center border-l-4 border-yellow-400">
                <div>
                    <span class="material-icons-outlined mr-2 text-yellow-600">warning</span>
                    <div>
                        <span class="text-lg font-medium text-yellow-800">Attenzione: Indagine terminata</span>
                        <p class="mt-1 text-yellow-700">Stai aggiungendo risposte a un'indagine che è già terminata.</p>
                    </div>
                </div>
            </div>
        {/if}
        <StepWizard initialStep="{1}">
            <StepWizard.Step num="{1}" let:nextStep>
				<div class="grid grid-cols-1 px-4 pt-4">
					<div class="flex col-start-2" style="text-align: end">
						<button disabled={!selectedSede?.id || selectedSede?.id == 0} type="button" class="btn-white h-14 w-14 sm:h-16 sm:w-16" on:click="{nextStep}">
							<span class="material-icons-outlined text-2xl leading-[3rem] sm:text-3xl sm:leading-[3.25rem]">arrow_forward_ios</span>
						</button>
					</div>
				</div>
                <span class="p-4">Seleziona la sede dal seguente menù a tendina</span>
                <div class="md:grid-cols-3 grid grid-cols-1">
                    {#if $loadingSedi}
                        <FieldLoading customClass="md:col-start-2" />
                    {:else}
                        <Select
                            label="{$_('page.survey.detail.location')}"
                            key="{(sede) => `${(sede && sede.id) || ''}`}"
                            bind:value="{selectedSede}"
                            class="md:col-start-2"
                        >
                            {#each $dataSedi.response || [] as sede (sede.id)}
                                {#if sede.denominazione}
                                    <Option
                                        value="{sede}"
                                        on:click="{() => {
                                            $data.sedeId = sede.id;
                                        }}"
                                    >{sede.denominazione}</Option>
                                {/if}
                            {/each}
                        </Select>
                    {/if}
                </div>
            </StepWizard.Step>
                <StepWizard.Step num="{2}" let:previousStep >
                    <div class="grid grid-cols-2 px-4 pt-4">
                        <div class="justify-self-start">
                            <button type="button" class="btn-white h-14 w-14 sm:h-16 sm:w-16" on:click="{previousStep}">
                                <span class="material-icons-outlined text-2xl leading-[3rem] sm:text-3xl sm:leading-[3.25rem]">arrow_back_ios</span>
                            </button>
                        </div>
                    </div>

                    <div class="md:grid-cols-4 md:gap-x-8 md:px-4 grid grid-cols-1 pt-4">
                        <!--
                        <Input label="{$_('page.survey.detail.code')}" disabled bind:value="{$data.codice}" class="md:col-span-2" />
                    -->
                        <Input label="{$_('page.survey.detail.description')}" disabled bind:value="{$data.descrizione}" class="md:col-span-2" />
                        
                        <div class="md:col-start-1 md:col-end-5 pt-4">
                            <TabBar {tabs} let:tab bind:active on:MDCTabBar:activated="{() => handleTabChange(active)}">
                                <Tab {tab} minWidth>
                                    <Icon class="material-icons">{tab.icon}</Icon>
                                    <Label>{tab.label}</Label>
                                </Tab>
                            </TabBar>

                            {#if active.label === 'Report'}
                                <SurveyReport
                                    bind:this={surveyReportComponent}
                                    stringifiedQuestionarioJson="{$data.questionario.questionarioJson}"
                                    risposteJson="{jsonResponses}"
                                />
                                <div class="flex justify-end mt-4">
                                <button disabled="{!jsonResponses.length || !!jsonError}" on:click="{submit}" class="btn-primary">Salva risposte</button>
                            </div>
                            {:else if active.label === 'JSON'}
                                <div class="flex justify-end mt-4 mb-2">
                                    <Button on:click={formatJson} variant="raised" class="ml-auto w-[250px]">
                                        <Label>Formatta JSON</Label>
                                    </Button>
                                </div>
                                <Input
                                    textarea
                                    bind:value={jsonData}
                                    on:input={handleJsonChange}
                                    input$placeholder="JSON delle risposte (array di oggetti JSON)"
                                    input$rows="20"
                                    class="w-full"
                                    invalid={!!jsonError}
                                />
                                <div class="mt-2 text-sm {jsonError ? 'text-red-500' : 'text-gray-600'}">
                                    {persistentHelperText || 'Inserisci un array di oggetti JSON per le risposte multiple'}
                                </div>
                            {/if}
                        </div>
                    </div>
            </StepWizard.Step>
        </StepWizard>
    {/if}
</Card>